import { Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import moment from 'moment';
import React, { PureComponent, Dispatch, Fragment } from 'react';
import { connect } from 'react-redux';
import { Actions } from '../../../../store/actions';
import { updateFollowup } from '../../../../store/actions/prescriptionPad';
import { upsertGenieSetting } from '../../../../store/actions/session';
import { GFollowUp } from '../../../../store/db';
import { DB_TABLES } from '../../../../store/dbTables';
import { getG } from '../../../../store/idbHandler';
import { GlobalState } from '../../../../store/reducers';
import { getFollowUp } from '../../../../store/selectors/prescriptionPad';
import { getIsFollowupAutoFillEnabled } from '../../../../store/selectors/session';
import { Followup } from '../../../../store/types/prescription';
import { getUnit } from '../../../../store/utils';
import { CHANNEL_GENIE, CHANNEL_SUGGESTION, SELECT, track } from '../../../../tracking';
import FollowUpInput from './FollowUpInput';

type Props = {
    dispatch: Dispatch<Actions>;
    followUp?: Followup;
    autoFillFromRxEnabled: boolean;
    rxDocId: string;
};

type State = {
    followUpNotes?: string;
    isFocussed?: boolean;
    gFollowUp?: GFollowUp[];
};

class FollowUp extends PureComponent<Props, State> {
    notesInput: HTMLTextAreaElement | null = null;
    state: State = {
        followUpNotes: this.props.followUp?.notes,
    };

    componentDidMount = () => {
        const { rxDocId } = this.props;
        const gFollowUp = getG<GFollowUp>(rxDocId, DB_TABLES['followUp'], 3);

        Promise.all([gFollowUp]).then((values) => {
            this.setState({ gFollowUp: values[0] });
        });
    };

    onSelect = (option: { label: string; value: number } | null) => {
        if (!option) {
            track('DELETE', { element: 'FollowUp' });
            this.props.dispatch(updateFollowup('', undefined, this.props.followUp?.notes));
            return;
        }

        const fDate = moment(new Date().toDateString()).add(option.value, 'day');
        this.props.dispatch(
            updateFollowup(fDate.toISOString(), undefined, this.props.followUp?.notes),
        );

        track(SELECT, { element: 'FollowUp', channel: CHANNEL_SUGGESTION });
    };

    onCalendarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.value || !moment(e.target.value).isValid()) {
            track('DELETE', { element: 'FollowUp', channel: 'calendar' });
            this.props.dispatch(updateFollowup('', undefined, this.props.followUp?.notes));
            return;
        }

        const fDate = moment(e.target.value);
        this.props.dispatch(
            updateFollowup(fDate.toISOString(), undefined, this.props.followUp?.notes),
        );

        track(SELECT, { element: 'FollowUp', channel: 'calendar' });
    };

    updateFollowDate = (f: GFollowUp) => {
        const fDate = moment(new Date().toDateString()).add(f.daysToAdd, 'day');
        this.props.dispatch(
            updateFollowup(fDate.toISOString(), undefined, this.props.followUp?.notes),
        );
        track(SELECT, { element: 'FollowUp', channel: CHANNEL_GENIE });
    };

    onFollowUpNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        this.setState({
            followUpNotes: e.target.value,
        });
    };

    onFollowUpInputBlur = () => {
        const { followUp } = this.props;

        this.setState({
            isFocussed: false,
        });

        if (
            (followUp?.notes?.toLowerCase() || '') ===
            (this.state.followUpNotes?.toLowerCase() || '')
        ) {
            return;
        }
        this.props.dispatch(
            updateFollowup(followUp?.date, followUp?.optionId, this.state.followUpNotes || ''),
        );
    };

    onFollowUpInputFocus = () => {
        this.setState({
            isFocussed: true,
        });
    };

    handleTickClick = () => {
        this.notesInput?.blur();
    };

    componentDidUpdate = (prevProps: Props): void => {
        if (prevProps.followUp?.notes !== this.props.followUp?.notes) {
            this.setState({ followUpNotes: this.props.followUp?.notes });
        }
    };

    handleAutoFillChange = (e: CheckboxChangeEvent) => {
        this.props.dispatch(
            upsertGenieSetting({
                autoFillFollowUp: e.target.checked,
            }),
        );
    };

    render() {
        const { followUp, autoFillFromRxEnabled } = this.props;
        const { followUpNotes, isFocussed, gFollowUp } = this.state;

        let value: { label: string; value: number } | null = null;

        const fDate = followUp?.date && moment(followUp.date);

        if (fDate) {
            const diffDate = fDate.diff(new Date().toDateString(), 'days');
            value = {
                label: `${diffDate} ${getUnit('days', diffDate.toString())}`,
                value: diffDate,
            };
        }

        return (
            <Fragment>
                <div className="space-y-8 pb-8">
                    <div className="flex justify-between">
                        <p className="font-700 text-14 padv2-hide">Follow Up After</p>
                        <div className="bg-white flex-1 mr-16 hidden padv2-show">
                            <FollowUpInput value={value} onSelect={this.onSelect} />
                        </div>
                        <input
                            className="follow-up-date cursor-pointer"
                            type="date"
                            onChange={this.onCalendarChange}
                            min={moment().format('YYYY-MM-DD')}
                            value={fDate ? fDate.format('YYYY-MM-DD') : ''}
                        />
                    </div>
                    <div>
                        <div className="bg-white padv2-hide">
                            <FollowUpInput value={value} onSelect={this.onSelect} />
                        </div>
                    </div>
                    <div>
                        <div className="flex-wrap flex justify-between items-center mt-14 gap-y-10">
                            <div>
                                {!followUp?.date && gFollowUp && gFollowUp.length > 0 && (
                                    <div className="">
                                        {gFollowUp.map((f) => (
                                            <span
                                                onClick={() => this.updateFollowDate(f)}
                                                key={f.id}
                                                role="button"
                                                className="mt-8 mr-8 dy-chip whitespace-wrap chip-show"
                                            >
                                                {f.label}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {fDate ? (
                                    <div className="mx-4">
                                        <span>
                                            <span
                                                className={
                                                    fDate.format('dd') === 'Su' ||
                                                    fDate.format('dd') === 'Sa'
                                                        ? 'text-status-error font-600'
                                                        : ''
                                                }
                                            >
                                                {fDate.format('dddd,')}
                                            </span>{' '}
                                            {fDate.format("DD MMM'YY")}
                                        </span>
                                    </div>
                                ) : null}
                            </div>
                            <div className="flex items-center">
                                <Checkbox
                                    onChange={this.handleAutoFillChange}
                                    checked={autoFillFromRxEnabled}
                                >
                                    Auto Fill from Rx
                                </Checkbox>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-24 space-y-8 relative">
                    <p className="font-700 text-14 padv2-hide">Follow Up Notes</p>
                    <textarea
                        ref={(ref) => (this.notesInput = ref)}
                        placeholder={'Add notes'}
                        className={`w-full px-16 py-6 leading-6 border border-neutral-7 focus:outline-none placeholder-neutral-4 text-14 rounded-4 focus:border-neutral-1 padv2-followup-notes`}
                        value={followUpNotes || ''}
                        onChange={this.onFollowUpNotesChange}
                        onBlur={this.onFollowUpInputBlur}
                        onFocus={this.onFollowUpInputFocus}
                        maxLength={200}
                    ></textarea>

                    <div className="absolute right-0 bottom-0">
                        {isFocussed && (
                            <div className="w-48 h-48 tick chip-show">
                                <div
                                    onClick={this.handleTickClick}
                                    className="flex flex-auto items-center justify-center h-full ripple"
                                >
                                    <svg
                                        style={{ pointerEvents: 'auto' }}
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        width="20"
                                        height="20"
                                        className="fill-actionPrimary chip-show"
                                    >
                                        <path d="M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z"></path>
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default connect((state: GlobalState) => ({
    followUp: getFollowUp(state),
    autoFillFromRxEnabled: getIsFollowupAutoFillEnabled(state),
}))(FollowUp);
